/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module TechSupportListPageModule
 */

/* eslint-disable max-classes-per-file */

import {
    AfterViewInit,
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    Inject,
    Input,
    NgModule,
    NO_ERRORS_SCHEMA,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import {
    L10nService,
    VIPModule,
} from '@vmw/ngx-vip';

import {
    AviPermissionResource,
    IALBServicesStatus,
    ITechSupport,
    TechSupportFsmState,
} from 'generated-types';

import {
    TechSupportGenerationService,
} from 'ng/modules/support/services/tech-support-generation.service';

import {
    CportalService,
} from 'ajs/modules/cportal/services/cportal.service';

import { portalStatus } from 'ajs/modules/cportal/constants/cportal.constants';
import { Auth } from 'ajs/modules/core/services/auth/auth.service';

import { AviDatePipe } from 'ng/modules/core/pipes';
import {
    TechSupportCollection,
    TTechSupportCollection,
} from 'ng/modules/collections/factories/tech-support.collection.factory';

import { TechSupportProfileItem } from 'ng/modules/items/factories/tech-support-profile.item.factory';

import {
    IAviCollectionDataGridConfig,
    IAviCollectionDataGridConfigField,
    IAviCollectionDataGridRow,
} from 'ng/modules/data-grid/components/avi-collection-data-grid/avi-collection-data-grid.types';

import {
    HttpWrapper,
    THttpWrapper,
} from 'ajs/modules/core/factories/http-wrapper/http-wrapper.service';

import { StateService } from '@uirouter/core';
import { SUPPORT_TECH_SUPPORT_DETAILS_READINESS } from 'state-names';

import './tech-support-list-page.component.less';

import * as globalL10n from 'global-l10n';

import {
    ClrAccordionModule,
    ClrIconModule,
    ClrSpinnerModule,
    ClrWizardModule,
} from '@clr/angular';

import { LoaderModule } from 'ng/modules/loader/loader.module';
import { SharedModule } from 'ng/shared/shared.module';
import { AviCardModule } from 'nsx-alb-ui-components';
import { AviFormsModule } from 'ng/modules/avi-forms/avi-forms.module';
import { DataGridModule } from 'ng/modules/data-grid/data-grid.module';
import { NotificationModule } from 'ng/modules/notification/notification.module';
import { CoreModule } from 'ng/modules/core/core.module';
import { AviAlertService } from 'ng/modules/core/services/avi-alert.service';
import { DownloadFileService } from 'ng/modules/core/services/download-file.service';
import { TechSupportTypes, type TechSupportItem } from 'ng/modules/items/factories/tech-support.item.factory';
import { isDistinct } from 'ng/shared/utils/ngrx-store.utils';

import {
    Subject,
    Subscription,
    takeUntil,
} from 'rxjs';

import { map } from 'rxjs/operators';
import * as l10n from './tech-support-list-page.l10n';

import { TechSupportPageCardComponent } from './tech-support-page-card/tech-support-page-card.component';
import { RecentTechSupportListComponent } from './recent-tech-support-list/recent-tech-support-list.component';

import { TechSupportWizardComponent } from './tech-support-wizard/tech-support-wizard.component';

import {
    TechSupportWizardPageComponent,
} from './tech-support-wizard/tech-support-wizard-page/tech-support-wizard-page.component';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const { ...globalL10nKeys } = globalL10n;

const TECH_SUPPORT_FILE_SERVICE_BASE_URL = '/api/fileservice?uri=controller://tech_support/';

const FSM_STATES_ALLOWING_DOWNLOAD = new Set([
    TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED,
    TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED_WITH_WARNINGS,
]);

/**
 * @description
 *     Tech Support List Page Component.
 *
 *     There is a check for 'PERMISSION_CONTROLLER' on the avi-collection-data-grid component since
 *     loading tech support requires PERMISSION_CONTROLLER.
 * @author Shahab Hashmi, alextsg, Dnyaneshwar Jadhav, Dhanashree Tilak
 */
@Component({
    selector: 'tech-support-list-page',
    templateUrl: './tech-support-list-page.component.html',
})
export class TechSupportListPageComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input('resolveTechSupportProfile')
    public techSupportProfileItem: TechSupportProfileItem;

    /**
     * Template Ref for Tech Support Results.
     */
    @ViewChild('techSupportResultsTemplateRef')
    public techSupportResultsTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Template Ref for Tech Support Expander Template Ref.
     */
    @ViewChild('techSupportExpanderTemplateRef')
    public techSupportExpanderTemplateRef: TemplateRef<HTMLElement>;

    /**
     * TemplateRef for Tech support resource name column for operation.
     */
    @ViewChild('resourceNameTemplateRef')
    public readonly resourceNameTemplateRef: TemplateRef<HTMLElement>;

    public readonly l10nKeys = l10nKeys;

    /**
     * Tech Support grid config.
     */
    public techSupportGridConfig: IAviCollectionDataGridConfig<TechSupportItem>;

    public recentTechSupport: ITechSupport;

    public loadingRecentTechSupport = true;

    public recentNodeType: string;

    /**
     * Avi cloud connected status.
     */
    public isAviCloudConnected = false;

    /**
     * Controller registration status.
     */
    public isControllerRegistered = false;

    /**
     * Techsupport grid requires `PERMISSION_TECHSUPPORT` and `PERMISSION_CONTROLLER`.
     * This value will be set to true if user does not have `PERMISSION_CONTROLLER`.
     */
    public showTechSupportGrid = false;

    public readonly techSupportState = {
        success: TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED,
        completedWithWarnings: TechSupportFsmState.TECHSUPPORT_FSM_COMPLETED_WITH_WARNINGS,
        error: TechSupportFsmState.TECHSUPPORT_FSM_ERROR,
        inProgress: TechSupportFsmState.TECHSUPPORT_FSM_IN_PROGRESS,
        warning: TechSupportFsmState.TECHSUPPORT_FSM_WARNING,
    };

    private readonly httpWrapper: HttpWrapper;

    private techSupportCollection: TechSupportCollection;

    private packageUpdateSubscription: Subscription;

    private ngDestroyed$ = new Subject();

    public readonly globalL10nKeys = globalL10nKeys;

    constructor(
        private readonly l10nService: L10nService,
        private readonly cportalService: CportalService,
        private readonly techSupportGenerationService: TechSupportGenerationService,
        private readonly aviDatePipe: AviDatePipe,
        private readonly $state: StateService,
        private readonly aviAlertService: AviAlertService,
        private readonly downloadFileService: DownloadFileService,
        @Inject(TechSupportCollection)
        TechSupportCollection: TTechSupportCollection,
        authService: Auth,
        @Inject(HttpWrapper)
        HttpWrapper: THttpWrapper,
    ) {
        l10nService.registerSourceBundles(dictionary);
        this.techSupportCollection = new TechSupportCollection();
        this.httpWrapper = new HttpWrapper();

        this.showTechSupportGrid = authService.isPrivilegeAllowed(
            AviPermissionResource.PERMISSION_CONTROLLER,
        );
    }

    /** @override */
    public ngOnInit(): void {
        this.setRegistrationStatus();
        this.packageUpdateSubscription = this.techSupportGenerationService.packagesInProgress$.pipe(
            takeUntil(this.ngDestroyed$),
            map((packages: ITechSupport[]) => packages.map(pkg => pkg.uuid).sort()),
            isDistinct(),
        ).subscribe(() => {
            this.techSupportCollection.load();
        });

        if (this.showTechSupportGrid) {
            this.initTechSupportCollectionGridConfig();
        }

        this.techSupportGenerationService.checkInprogressPackage();
        this.techSupportGenerationService.getRecentTechSupport().subscribe((recentTechSupport: ITechSupport) => {
            this.loadingRecentTechSupport = false;

            if (recentTechSupport) {
                this.recentTechSupport = recentTechSupport;
                this.recentNodeType = this.getSupportTypeLabel(recentTechSupport?.level);
            } else {
                this.recentTechSupport = null;
            }
        });
    }

    /** @override */
    public ngAfterViewInit(): void {
        if (this.showTechSupportGrid) {
            this.techSupportGridConfig = {
                ...this.techSupportGridConfig,
                expandedContentTemplateRef: this.techSupportExpanderTemplateRef,
                fields: this.getTechSupportGridFields(),
            };
        }
    }

    /**
     * Download tech support package with error handling.
     * Perform a HEAD request to verify the file is accessible before opening the
     * download URL in a new tab — avoids presenting a blank/error page to the user
     * when the package has not yet been synced to the file service.
     */
    private async downloadTechSupportPackage(row: TechSupportItem): Promise<void> {
        try {
            const fileName = row.getFileName();
            const url = `${TECH_SUPPORT_FILE_SERVICE_BASE_URL}${fileName}`;
            const fileExists = await this.downloadFileService.checkFileExists(url);

            if (!fileExists) {
                throw new Error(this.l10nService.getMessage(l10nKeys.fileNotFoundErrorLabel));
            }

            window.open(url, '_blank');
        } catch (error: any) {
            const errorMessage = error?.message || error?.data?.error ||
                this.l10nService.getMessage(globalL10nKeys.downloadFileErrorLabel);

            this.aviAlertService.throw(errorMessage);
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.techSupportCollection.destroy();
        this.techSupportGenerationService.stopTechSupportPackages();
        this.ngDestroyed$.next(true);
        this.ngDestroyed$.complete();
    }

    /**
     * Return total number of items from the collection.
     */
    public get completePackagesCount(): number {
        return this.techSupportCollection.getTotalNumberOfItems();
    }

    public editTechSupport(): void {
        this.techSupportProfileItem.edit();
    }

    public isTechSupportProfileItemPresent(): boolean {
        return Boolean(this.techSupportProfileItem);
    }

    /**
     * Get registration status of the controller to show or hide Support Case column in
     * tech support collection grid.
     */
    private setRegistrationStatus(): void {
        const albServicesStatus = this.cportalService.getRegistrationStatusDetails();

        this.setStatusFlags(albServicesStatus);
    }

    /**
     * Update Controller registration and Avi cloud connectivity status
     */
    private setStatusFlags(data: IALBServicesStatus): void {
        const { ALBSERVICES_REGISTERED, ALBSERVICES_CONNECTED } = portalStatus;

        this.isControllerRegistered = data?.registration_status === ALBSERVICES_REGISTERED;
        this.isAviCloudConnected = data?.connectivity_status === ALBSERVICES_CONNECTED;
    }

    /**
     * Initializes collection and creates config for tech support table.
     */
    private initTechSupportCollectionGridConfig(): void {
        const { objectName } = this.techSupportCollection;
        const { l10nService } = this;

        this.techSupportGridConfig = {
            id: `${objectName}-list-page`,
            collection: this.techSupportCollection,
            fields: [],
            singleactions: [{
                label: l10nService.getMessage(l10nKeys.viewReportLabel),
                id: 'view-report',
                onClick: row => this.redirectToTechSupportDetailsPage(row, undefined),
            }, {
                label: l10nService.getMessage(l10nKeys.downloadPackageLabel),
                shape: 'download',
                id: 'download',
                onClick: row => {
                    this.downloadTechSupportPackage(row);
                },
                disabled: row => this.isDownloadDisabled(row),
            }, {
                label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                shape: 'trash',
                id: 'trash',
                onClick: row => {
                    row.drop().then(() => {
                        this.techSupportGenerationService.checkInprogressPackage();
                    });
                },
            }],
            multipleactions: [
                {
                    label: l10nService.getMessage(globalL10nKeys.deleteLabel),
                    id: 'delete',
                    onClick: rows => {
                        rows.map(row => row.drop());
                        setTimeout(() => this.techSupportGenerationService.checkInprogressPackage());
                    },
                },
            ],
            layout: {
                placeholderMessage: l10nService.getMessage(globalL10nKeys.noItemsFoundLabel),
                hideEdit: true,
                hideDelete: true,
                hideReferenceCheckAction: true,
                hideSearch: true,
                createButtonLabel: l10nService.getMessage(l10nKeys.generateButtonLabel),
            },
        };
    }

    /**
     * Disable row when resultStatus not present.
     */
    private isDownloadDisabled(row: TechSupportItem): boolean {
        const { state } = row.getConfig();

        if (!state) {
            return true;
        }

        const { state: fsmState } = state.config;

        return !FSM_STATES_ALLOWING_DOWNLOAD.has(fsmState);
    }

    /**
     * Navigate to Tech Support Report detail page.
     */
    public redirectToTechSupportDetailsPage(row: TechSupportItem, event: Event | undefined): void {
        if (event) {
            event.preventDefault();
        }

        const params: Record<string, string> = {
            reportId: row.data.config.uuid,
        };

        this.$state.go(SUPPORT_TECH_SUPPORT_DETAILS_READINESS, params);
    }

    /**
     * Return fields for Tech Support table.
     */
    private getTechSupportGridFields(): IAviCollectionDataGridConfigField[] {
        const { l10nService } = this;

        const fields: IAviCollectionDataGridConfigField[] = [
            {
                label: l10nService.getMessage(l10nKeys.columnTitleCreationDate),
                id: 'creation-date',
                transform: (row: IAviCollectionDataGridRow) => row.getConfig().start_time || '-',
            },
            {
                label: l10nService.getMessage(globalL10nKeys.typeLabel),
                id: 'type',
                transform: (row: IAviCollectionDataGridRow) => this.getSupportTypeLabel(row.getConfig().level),
            },
            {
                label: l10nService.getMessage(l10nKeys.columnTitleResourceName),
                id: 'resource-name',
                templateRef: this.resourceNameTemplateRef,
            },
            {
                label: l10nService.getMessage(globalL10nKeys.durationLabel),
                id: 'duration',
                transform: (row: IAviCollectionDataGridRow) =>
                    this.techSupportGenerationService.formatDuration(row.getConfig().duration),
            },
            {
                label: l10nService.getMessage(l10nKeys.columnTitleSize),
                id: 'size',
                transform: (row: IAviCollectionDataGridRow) => row.getConfig().size || '-',
            },
        ];

        if (this.isAviCloudConnected && this.isControllerRegistered) {
            fields.push({
                label: l10nService.getMessage(l10nKeys.columnTitleSupportCase),
                id: 'support-case',
                transform: (row: IAviCollectionDataGridRow) => {
                    let caseNumber = '';
                    const { caseDetails } = row.getConfig();

                    if (caseDetails && 'case_number' in caseDetails) {
                        caseNumber = caseDetails.case_number;
                    }

                    return caseNumber;
                },
            });
        }

        fields.push({
            label: this.l10nService.getMessage(l10nKeys.columnTitleResults),
            id: 'results',
            templateRef: this.techSupportResultsTemplateRef,
        });

        return fields;
    }

    /**
     * Return corresponding l10n value to Tech Support Type.
     */
    public getSupportTypeLabel = (type: string): string => {
        switch (type) {
            case TechSupportTypes.DEBUGLOGS.toLowerCase():
                return this.l10nService.getMessage(l10nKeys.debugLogsLabel);
            case TechSupportTypes.CLUSTERING:
                return this.l10nService.getMessage(l10nKeys.clusteringLabel);
            case TechSupportTypes.METRICSLOGS.toLowerCase():
                return this.l10nService.getMessage(l10nKeys.metricsLogsLabel);
            case TechSupportTypes.GSLB:
                return this.l10nService.getMessage(l10nKeys.gslbLabel);
            case TechSupportTypes.PLACEMENT:
                return this.l10nService.getMessage(l10nKeys.placementLabel);
            case TechSupportTypes.UPGRADE:
                return this.l10nService.getMessage(globalL10nKeys.upgradeLabel);
            case TechSupportTypes.POOL:
                return this.l10nService.getMessage(globalL10nKeys.poolLabel);
            case TechSupportTypes.VIRTUALSERVICE.toLowerCase():
                return this.l10nService.getMessage(globalL10nKeys.virtualServiceLabel);
            case TechSupportTypes.SERVICEENGINE.toLowerCase():
                return this.l10nService.getMessage(globalL10nKeys.serviceEngineLabel);
            case TechSupportTypes.PORTAL:
                return this.l10nService.getMessage(l10nKeys.portalLabel);
            default:
                return '-';
        }
    };
}

@NgModule({
    declarations: [
        TechSupportListPageComponent,
        TechSupportPageCardComponent,
        TechSupportWizardComponent,
        TechSupportWizardPageComponent,
        RecentTechSupportListComponent,
    ],
    imports: [
        AviCardModule,
        AviFormsModule,
        SharedModule,
        ClrAccordionModule,
        ClrIconModule,
        ClrSpinnerModule,
        ClrWizardModule,
        DataGridModule,
        CoreModule,
        LoaderModule,
        NotificationModule,
        VIPModule.forChild(),
    ],
    schemas: [
        CUSTOM_ELEMENTS_SCHEMA,
        NO_ERRORS_SCHEMA,
    ],
})
/* eslint-disable-next-line no-unused-vars */
class TechSupportListPageModule { }
